<script lang="ts">
  import { navigate } from '@shared/router/navigation';
  import type { ProductListQuery } from '@shared/types/product';
  import type { Product } from '@domain/product/model';

  type SortField = NonNullable<ProductListQuery['sort_by']>;
  type SortDir = NonNullable<ProductListQuery['sort_dir']>;

  interface Props {
    products: Product[];
    sortBy: SortField;
    sortDir: SortDir;
    onSort: (field: SortField) => void;
    allowReorder?: boolean;
    onReorder?: (productIds: Product['id'][]) => void;
  }

  let {
    products,
    sortBy,
    sortDir,
    onSort,
    allowReorder = false,
    onReorder,
  }: Props = $props();

  type DropPosition = 'before' | 'after';

  interface DropTarget {
    id: Product['id'];
    position: DropPosition;
  }

  let draggingId = $state<Product['id'] | null>(null);
  let dropTarget = $state<DropTarget | null>(null);

  interface Column {
    key: SortField | null;
    label: string;
  }

  // 競合は詳細「競合ソリューション」タブへ、価格は詳細「概要」タブへ集約し、
  // 一覧は運用に必要な列のみに絞る。
  const columns: Column[] = [
    { key: 'name', label: '自社ソリューション名' },
    { key: null, label: '解決課題' },
    { key: null, label: '利用中' },
    { key: null, label: '商談中' },
    { key: null, label: '失注' },
  ];

  function indicator(field: SortField | null): string {
    if (field === null || field !== sortBy) return '';
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  }

  // SPA 内遷移。 アンカーの href をそのまま使う。 id だけ取り出して
  // `/products/${id}` を再構築すると usage 件数リンクの ?tab=usage&status=... が
  // 落ちるため、 href を単一の遷移先ソースにする。
  function gotoDetail(ev: MouseEvent) {
    ev.preventDefault();
    const href = (ev.currentTarget as HTMLAnchorElement).getAttribute('href');
    if (href) navigate(href);
  }

  function chips(list: string[] | null | undefined): {
    shown: string[];
    rest: number;
  } {
    const items = list ?? [];
    if (items.length === 0) return { shown: [], rest: 0 };
    const limit = 3;
    return {
      shown: items.slice(0, limit),
      rest: Math.max(items.length - limit, 0),
    };
  }

  function currentProductIds(): Product['id'][] {
    return products.map((product) => product.id);
  }

  function sameOrder(productIds: readonly Product['id'][]): boolean {
    return (
      productIds.length === products.length &&
      productIds.every((id, index) => id === products[index]?.id)
    );
  }

  function applyOrder(productIds: Product['id'][]): void {
    if (sameOrder(productIds)) return;
    onReorder?.(productIds);
  }

  function reorderByIndex(fromIndex: number, toIndex: number): void {
    if (!allowReorder || !onReorder) return;
    if (fromIndex < 0 || fromIndex >= products.length) return;
    const productIds = currentProductIds();
    const moved = productIds[fromIndex];
    if (moved === undefined) return;
    productIds.splice(fromIndex, 1);
    const boundedIndex = Math.max(0, Math.min(toIndex, productIds.length));
    productIds.splice(boundedIndex, 0, moved);
    applyOrder(productIds);
  }

  function reorderAroundTarget(
    sourceId: Product['id'],
    targetId: Product['id'],
    position: DropPosition,
  ): void {
    if (!allowReorder || !onReorder || sourceId === targetId) return;
    const productIds = currentProductIds();
    const fromIndex = productIds.indexOf(sourceId);
    const targetIndex = productIds.indexOf(targetId);
    if (fromIndex < 0 || targetIndex < 0) return;
    const moved = productIds[fromIndex];
    if (moved === undefined) return;

    let insertIndex = position === 'before' ? targetIndex : targetIndex + 1;
    productIds.splice(fromIndex, 1);
    if (fromIndex < insertIndex) insertIndex -= 1;
    insertIndex = Math.max(0, Math.min(insertIndex, productIds.length));
    productIds.splice(insertIndex, 0, moved);
    applyOrder(productIds);
  }

  function dropPosition(event: DragEvent): DropPosition {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    return event.clientY < rect.top + rect.height / 2 ? 'before' : 'after';
  }

  function handleDragStart(event: DragEvent, product: Product): void {
    if (!allowReorder || !onReorder) {
      event.preventDefault();
      return;
    }
    draggingId = product.id;
    dropTarget = null;
    event.dataTransfer?.setData('text/plain', product.id);
    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
  }

  function handleDragOver(event: DragEvent, targetId: Product['id']): void {
    if (!allowReorder || draggingId === null || draggingId === targetId) {
      dropTarget = null;
      return;
    }
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
    dropTarget = { id: targetId, position: dropPosition(event) };
  }

  function handleDragLeave(event: DragEvent, targetId: Product['id']): void {
    const nextTarget = event.relatedTarget;
    if (
      nextTarget instanceof Node &&
      (event.currentTarget as HTMLElement).contains(nextTarget)
    ) {
      return;
    }
    if (dropTarget?.id === targetId) dropTarget = null;
  }

  function handleDrop(event: DragEvent, targetId: Product['id']): void {
    if (!allowReorder) return;
    event.preventDefault();
    const sourceId =
      draggingId ??
      (event.dataTransfer?.getData('text/plain') as Product['id'] | undefined);
    const position = dropPosition(event);
    draggingId = null;
    dropTarget = null;
    if (!sourceId) return;
    reorderAroundTarget(sourceId, targetId, position);
  }

  function handleDragEnd(): void {
    draggingId = null;
    dropTarget = null;
  }

  function handleHandleKeydown(event: KeyboardEvent, index: number): void {
    if (!allowReorder || !onReorder) return;
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      reorderByIndex(index, index - 1);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      reorderByIndex(index, index + 1);
    } else if (event.key === 'Home') {
      event.preventDefault();
      reorderByIndex(index, 0);
    } else if (event.key === 'End') {
      event.preventDefault();
      reorderByIndex(index, products.length - 1);
    }
  }

  function isDropTarget(id: Product['id'], position: DropPosition): boolean {
    return dropTarget?.id === id && dropTarget.position === position;
  }
</script>

<div class="table-scroll">
  {#if products.length === 0}
    <div class="empty">該当する自社ソリューションがありません</div>
  {:else}
    <table data-testid="products-table">
      <colgroup>
        {#if allowReorder}
          <col class="col-drag-handle-width" />
        {/if}
        <col class="col-product-name-width" />
        <col class="col-problems-width" />
        <col class="col-count-width" />
        <col class="col-count-width" />
        <col class="col-count-width" />
      </colgroup>
      <thead>
        <tr>
          {#if allowReorder}
            <th class="drag-head">順序</th>
          {/if}
          {#each columns as col (col.label)}
            {#if col.key}
              <th>
                <button
                  type="button"
                  class="sort"
                  onclick={() => onSort(col.key as SortField)}
                >
                  {col.label}{indicator(col.key)}
                </button>
              </th>
            {:else}
              <th>{col.label}</th>
            {/if}
          {/each}
        </tr>
      </thead>
      <tbody>
        {#each products as p, index (p.id)}
          {@const probs = chips(p.problem_names)}
          <tr
            class:isDragging={draggingId === p.id}
            class:dropBefore={isDropTarget(p.id, 'before')}
            class:dropAfter={isDropTarget(p.id, 'after')}
            ondragover={(ev) => handleDragOver(ev, p.id)}
            ondragleave={(ev) => handleDragLeave(ev, p.id)}
            ondrop={(ev) => handleDrop(ev, p.id)}
          >
            {#if allowReorder}
              <td class="drag-cell">
                <button
                  type="button"
                  class="drag-handle"
                  draggable={allowReorder}
                  aria-label={`${p.name}をドラッグして並び替え`}
                  title="ドラッグして並び替え"
                  data-testid="product-drag-handle"
                  data-product-id={p.id}
                  ondragstart={(ev) => handleDragStart(ev, p)}
                  ondragend={handleDragEnd}
                  onkeydown={(ev) => handleHandleKeydown(ev, index)}
                >
                  ⋮⋮
                </button>
              </td>
            {/if}
            <td>
              <a
                href={`/products/${p.id}`}
                class="primary-link"
                data-testid="solution-list-link"
                onclick={(ev) => gotoDetail(ev)}
              >
                <strong>{p.name}</strong>
              </a>
            </td>
            <td class="col-problems">
              {#if probs.shown.length === 0}
                <span class="muted">-</span>
              {:else}
                {#each probs.shown as pn, idx (idx)}
                  <span class="problem-chip">{pn}</span>
                {/each}
                {#if probs.rest > 0}
                  <span class="muted">+{probs.rest}</span>
                {/if}
              {/if}
            </td>
            <td class="numeric">
              {#if p.usage && p.usage.active > 0}
                <a
                  href={`/products/${p.id}?tab=usage&status=active`}
                  onclick={(ev) => gotoDetail(ev)}
                >
                  {p.usage.active}
                </a>
              {:else}
                <span class="muted">0</span>
              {/if}
            </td>
            <td class="numeric">
              {#if p.usage && p.usage.in_progress > 0}
                <a
                  href={`/products/${p.id}?tab=usage&status=opportunity`}
                  onclick={(ev) => gotoDetail(ev)}
                >
                  {p.usage.in_progress}
                </a>
              {:else}
                <span class="muted">0</span>
              {/if}
            </td>
            <td class="numeric">
              {#if p.usage && p.usage.lost > 0}
                <a
                  href={`/products/${p.id}?tab=usage&status=lost`}
                  onclick={(ev) => gotoDetail(ev)}
                >
                  {p.usage.lost}
                </a>
              {:else}
                <span class="muted">0</span>
              {/if}
            </td>
          </tr>
        {/each}
      </tbody>
    </table>
  {/if}
</div>

<style>
  .table-scroll {
    max-height: max(360px, calc(100vh - 280px));
    overflow: auto;
    border-top: 1px solid var(--border, #e3e6eb);
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
  }
  table {
    width: 100%;
    table-layout: fixed;
  }
  .col-drag-handle-width {
    width: 56px;
  }
  .col-product-name-width {
    width: 28%;
  }
  .col-problems-width {
    width: auto;
  }
  .col-count-width {
    width: 84px;
  }
  th,
  td {
    vertical-align: middle;
    overflow-wrap: anywhere;
  }
  thead th {
    position: sticky;
    top: 0;
    z-index: 3;
    background: #f9fafb;
    box-shadow: 0 1px 0 var(--border, #e3e6eb);
  }
  th button.sort {
    background: transparent;
    border: 0;
    padding: 0;
    cursor: pointer;
    color: inherit;
    font: inherit;
  }
  th button.sort:hover {
    color: var(--primary, #2563eb);
  }
  .drag-head {
    text-align: center;
  }
  .drag-cell {
    text-align: center;
    white-space: nowrap;
  }
  .drag-handle {
    width: 32px;
    height: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid transparent;
    border-radius: 6px;
    background: transparent;
    color: #6b7280;
    cursor: grab;
    font: inherit;
    font-size: 18px;
    letter-spacing: 0;
    line-height: 1;
    user-select: none;
  }
  .drag-handle:hover,
  .drag-handle:focus-visible {
    border-color: var(--border, #e3e6eb);
    background: #f9fafb;
    color: var(--primary, #2563eb);
    outline: none;
  }
  .drag-handle:active {
    cursor: grabbing;
  }
  tbody tr.isDragging {
    opacity: 0.48;
  }
  tbody tr.dropBefore td {
    box-shadow: inset 0 2px 0 var(--primary, #2563eb);
  }
  tbody tr.dropAfter td {
    box-shadow: inset 0 -2px 0 var(--primary, #2563eb);
  }
  .primary-link {
    color: var(--primary, #2563eb);
    text-decoration: none;
  }
  .primary-link:hover {
    text-decoration: underline;
  }
  .muted {
    color: var(--muted, #6b7280);
  }
  .problem-chip {
    display: inline-block;
    padding: 2px 8px;
    margin-right: 4px;
    background: #fef3c7;
    color: #92400e;
    border-radius: 999px;
    font-size: 12px;
    line-height: 1.35;
    max-width: 100%;
    overflow-wrap: anywhere;
  }
  td.numeric {
    text-align: center;
  }
  td.numeric {
    font-variant-numeric: tabular-nums;
  }
</style>
